import React, { useContext, useState, useEffect } from 'react'
import { getGuestCartContents, resetGuestCart } from '#src/services/cart'
import {
  ShoppingCartOrderFragment,
  GetShoppingCartComponent,
  useSyncShoppingCartMutation,
  useAddItemsToOrderMutation,
  useShoppingCartSubscription
} from '#src/generated/graphql'
import { IdentityContext } from '#src/containers/Identity'

export type LoggedInCartContextType = {
  order?: ShoppingCartOrderFragment
  refetch?: () => void
  loading?: boolean
}

interface LoggedInCartOrderObserverProps {
  children: ({ order }: LoggedInCartContextType) => React.ReactNode
}

const LoggedInCartOrderObserver: React.SFC<LoggedInCartOrderObserverProps> = ({ children }) => {
  const { myself } = useContext(IdentityContext)

  // Since this cart observer is only logged in users, this is a hard failure
  if (!myself) {
    throw new Error('Attempted render of LoggedInCartObserver as a guest')
  }

  const [orderId, setOrderId] = useState()
  const [syncShoppingCart] = useSyncShoppingCartMutation()
  const [addItemsToOrder] = useAddItemsToOrderMutation()

  useEffect(() => {
    /**
     * State management strategy:
     *
     *  1. Use the `syncCart` mutation to get current order ID (once)
     *  2. Use a GraphQL subscription to subscribe to all Cart changes for as long as the user is logged in
     *  3. Check the local storage to see if there is any thing from the guest cart we need to sync up (once)
     *  4. On every subsequent shopping cart component mount, a 'refetch' is called to true up the order
     */
    syncShoppingCart().then(({ data }) => {
      if (data && data.syncOrder) {
        setOrderId(data.syncOrder.order.id)
      }
    })

    const guestCartContents = getGuestCartContents()
    if (guestCartContents && guestCartContents.length > 0) {
      // Run a mutation to add these items to our current cart
      addItemsToOrder({
        variables: {
          input: {
            sellables: guestCartContents.map(({ id, quantity }) => ({
              sellableId: id,
              quantity
            }))
          }
        }
      }).then(() => {
        // Wipe the local storage, we don't need this cart entry anymore
        resetGuestCart()
      })
    }
  }, [])

  // Kick off the mounting of the subscription
  useShoppingCartSubscription()

  return (
    // The purpose of this query is to read cache updates. By the time it runs the cache will be updated
    <GetShoppingCartComponent
      skip={!orderId || !myself}
      variables={{ id: orderId }}
      partialRefetch={true}
    >
      {({ data, loading }) => {
        // Pass through undefined if data object does not exist so that child can handle loading state
        return (
          <>
            {children({
              order: (data && data.order) || undefined,
              loading,
              refetch: () => {
                syncShoppingCart().then(({ data }) => {
                  if (data && data.syncOrder) {
                    setOrderId(data.syncOrder.order.id)
                  }
                })
              }
            })}
          </>
        )
      }}
    </GetShoppingCartComponent>
  )
}

export default LoggedInCartOrderObserver
